import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Modal, ModalProps, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Form, Formik, useFormikContext, useField } from 'formik';
import { map } from 'lodash';
import { FormGroupControl, FormGroupSelect, FormGroupControlProps } from '@components/Base';
import { HealthClaimCostItem } from '@models/HealthClaimCostItem';
import { BenefitLimit } from '@models/BenefitLimit';
import { mergeInitialValues } from '@utils/formik';
import { healthClaimCostItemSchema, FormValues } from './schema';

const GROUP_PROPS = { className: 'mb-3' };

const OverLimitAmountFormGroupControl = ({ name, ...props }: FormGroupControlProps) => {
  const {
    values: { benefitLimitId, declaredAmount, approvedAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field, _meta] = useField(name);

  useEffect(() => {
    if (benefitLimitId === '') {
      setFieldValue(name, 0);
      return;
    }

    if (touched.declaredAmount && touched.approvedAmount && !errors.approvedAmount && !errors.declaredAmount) {
      const overLimitAmount = Number(declaredAmount) - Number(approvedAmount);
      setFieldValue(name, Math.max(overLimitAmount, 0));
    }
  }, [declaredAmount, approvedAmount, touched.declaredAmount, touched.approvedAmount, setFieldValue, name, errors]);

  return <FormGroupControl {...field} {...props} />;
};

const DeductibleCopayAmountFormGroupControl = ({
  benefitLimits,
  name,
  ...props
}: { benefitLimits: BenefitLimit[] } & FormGroupControlProps) => {
  const {
    values: { benefitLimitId, declaredAmount, approvedAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field, _meta] = useField(name);
  const [helpText, setHelpText] = useState('');
  const [selectedBenefitLimit, setSelectedBenefitLimit] = useState<BenefitLimit | null>(null);

  useEffect(() => {
    setSelectedBenefitLimit(benefitLimits.find((limit) => limit.id === Number(benefitLimitId)) || null);
  }, [benefitLimitId]);

  useEffect(() => {
    if (!selectedBenefitLimit) {
      setFieldValue(name, 0);
      return;
    }

    if (touched.declaredAmount && !errors.declaredAmount) {
      if (selectedBenefitLimit.copayPercentage > 0) {
        const copayAmount = (Number(declaredAmount) * selectedBenefitLimit.copayPercentage) / 100;
        setFieldValue(name, copayAmount);
      } else {
        setFieldValue(name, selectedBenefitLimit.deductibleAmount);
      }
    }
  }, [selectedBenefitLimit, declaredAmount, touched.declaredAmount, setFieldValue, name, errors]);

  useEffect(() => {
    if (Number(selectedBenefitLimit?.copayPercentage) > 0) {
      setHelpText(`Co-pay: ${selectedBenefitLimit?.copayPercentage}%`);
    } else if (Number(selectedBenefitLimit?.deductibleAmount) > 0) {
      setHelpText(`Deductible amount: ${selectedBenefitLimit?.deductibleAmount}`);
    } else {
      setHelpText('');
    }
  }, [selectedBenefitLimit]);

  return <FormGroupControl {...field} {...props} help={helpText} />;
};

const UncoveredExpensesAmountFormGroupControl = ({ name, ...props }: FormGroupControlProps) => {
  const {
    values: { customSubBenefit, declaredAmount, approvedAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field, _meta] = useField(name);

  useEffect(() => {
    if (customSubBenefit.trim() === '') {
      setFieldValue(name, 0);
      return;
    }

    if (touched.declaredAmount && touched.approvedAmount && !errors.declaredAmount && !errors.approvedAmount) {
      const uncoveredExpensesAmount = Number(declaredAmount) - Number(approvedAmount);
      setFieldValue(name, uncoveredExpensesAmount);
    }
  }, [
    customSubBenefit,
    declaredAmount,
    approvedAmount,
    touched.declaredAmount,
    touched.approvedAmount,
    setFieldValue,
    name,
    errors,
  ]);

  return <FormGroupControl {...field} {...props} />;
};

const TotalCoveredAmountFormGroupControl = ({ name, ...props }: FormGroupControlProps) => {
  const {
    values: { benefitLimitId, declaredAmount, approvedAmount, deductibleCopayAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field, _meta] = useField(name);

  useEffect(() => {
    if (touched.approvedAmount && !errors.approvedAmount) {
      const amountAfterDeductibleCopayAmount = Number(declaredAmount) - Number(deductibleCopayAmount);
      const totalCoveredAmount = !!benefitLimitId
        ? amountAfterDeductibleCopayAmount > Number(approvedAmount)
          ? Number(approvedAmount)
          : amountAfterDeductibleCopayAmount
        : approvedAmount;
      setFieldValue(name, totalCoveredAmount);
    }
  }, [
    declaredAmount,
    approvedAmount,
    deductibleCopayAmount,
    touched.approvedAmount,
    benefitLimitId,
    setFieldValue,
    name,
    errors,
  ]);

  return <FormGroupControl {...field} {...props} />;
};

type Props = ModalProps & {
  onSubmit: (values: FormValues) => void;
  healthClaimCostItem: HealthClaimCostItem | null;
  benefitLimits: BenefitLimit[];
  hospitalLengthOfStay: number;
};

const CostItemFormModal = ({ onSubmit, benefitLimits, healthClaimCostItem, hospitalLengthOfStay, ...props }: Props) => {
  const { t } = useTranslation();
  const initialValues: FormValues = mergeInitialValues(
    healthClaimCostItemSchema.getDefault(),
    { benefitLimitId: healthClaimCostItem?.benefitLimit?.id },
    healthClaimCostItem,
  );

  return (
    <Modal {...props} size="lg" centered>
      <Formik initialValues={initialValues} validationSchema={healthClaimCostItemSchema} onSubmit={onSubmit}>
        {({ isSubmitting }) => {
          return (
            <Form>
              <Modal.Header closeButton>
                <Modal.Title>Cost Item</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Alert variant="info">Hospital length of stay (Nights/Days): {hospitalLengthOfStay}</Alert>

                <Row>
                  <Col>
                    <FormGroupSelect name="benefitLimitId" label={`Sub benefit*`} groupProps={GROUP_PROPS} asRow>
                      <option value="">Please select</option>
                      {map(benefitLimits, ({ id, subBenefit }) => (
                        <option key={subBenefit} value={id}>
                          {subBenefit}
                        </option>
                      ))}
                    </FormGroupSelect>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl
                      asRow
                      name="customSubBenefit"
                      label={`Custom sub benefit`}
                      help={`If the sub benefit you want is not listed, you can specify it here.`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl
                      required
                      name="declaredAmount"
                      label={`Amount declared`}
                      groupProps={GROUP_PROPS}
                      asRow
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl
                      required
                      name="approvedAmount"
                      label={`Approved amount (cover limit)`}
                      groupProps={GROUP_PROPS}
                      asRow
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <UncoveredExpensesAmountFormGroupControl
                      required
                      name="uncoveredExpensesAmount"
                      label={`Expenses not covered`}
                      groupProps={GROUP_PROPS}
                      asRow
                      disabled
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <DeductibleCopayAmountFormGroupControl
                      benefitLimits={benefitLimits}
                      required
                      asRow
                      disabled
                      name="deductibleCopayAmount"
                      label={`Deductible or co-pay amount`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <OverLimitAmountFormGroupControl
                      required
                      asRow
                      disabled
                      name="overLimitAmount"
                      label={`Over limit amount`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TotalCoveredAmountFormGroupControl
                      required
                      asRow
                      disabled
                      name="totalCoveredAmount"
                      label={`Total amount covered`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl name="remark" label={`Remark`} groupProps={GROUP_PROPS} asRow />
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={props.onHide}>
                  {t('confirmModal.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {t('common:submit')}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export { CostItemFormModal };
